import { US_STATE_CODES } from '../enums';

/**
 * Reports (reference PC224 > All Reports PC428) and the log screens that share their shape.
 *
 * Every report is a definition: heading, permission code, filters and columns. The server
 * has one data provider per definition; one generic screen renders them all. Headings,
 * filter labels, option lists and column headers are the reference's (product inventory,
 * appendix 11.2 and 11.3, and appendix part 10). The reference has 50 reports; a report is
 * defined here once this application holds the data it shows. For 11 of them the reference
 * showed filters but no result columns (they load after a search on live data); those
 * definitions say "Columns are ours".
 */

export type ReportFilter =
  /** Master Agent / Distributor / Retailer / Employee selects; sends `agentId`. */
  | { type: 'agentChain' }
  /** One date-range field; sends `<key>From` and `<key>To` (YYYY-MM-DD). */
  | { type: 'dateRange'; key: string; label: string }
  | {
      type: 'select';
      key: string;
      label: string;
      options: readonly string[];
      /** Label of the "nothing chosen" entry; absent means the first option is preselected. */
      emptyLabel?: string;
      required?: boolean;
      /** Several options may be chosen (the reference's multi-selects); sent comma-separated. */
      multiple?: boolean;
      /** A filter added after a deployment: shown only while this release feature is on. */
      feature?: string;
    }
  /** US state select; sends the two-letter code. */
  | { type: 'state'; key: string; label: string; allLabel: string }
  /** Carrier select filled from the carrier list; sends the carrier code. */
  | { type: 'carrier'; key: string; label: string }
  | {
      type: 'text';
      key: string;
      label: string;
      maxLength?: number;
      /** A filter added after a deployment: shown only while this release feature is on. */
      feature?: string;
    }
  /** A tick box; sends `Y` when ticked. */
  | { type: 'checkbox'; key: string; label: string; feature?: string }
  /** A multi-select of the user groups; sends the chosen group ids comma-separated. */
  | { type: 'userGroup'; key: string; label: string; feature?: string };

export type ReportColumnKind = 'text' | 'date' | 'datetime' | 'money' | 'number' | 'yesno';

export interface ReportColumn {
  key: string;
  header: string;
  kind?: ReportColumnKind;
  /** A column added after a deployment: shown and exported only while this release feature is on. */
  feature?: string;
}

export const REPORT_GROUPS = [
  ['management', 'Management Reports'],
  ['customer', 'Customer Reports'],
  ['agent', 'Agent Reports'],
] as const;
/** `log` and `screen` definitions have their own menu entries and are not in the catalogue. */
export type ReportGroup = (typeof REPORT_GROUPS)[number][0] | 'log' | 'screen';

export interface ReportDef {
  key: string;
  group: ReportGroup;
  /** Name in the All Reports catalogue. */
  name: string;
  /** Heading of the report page. */
  heading: string;
  code: string;
  filters: readonly ReportFilter[];
  columns: readonly ReportColumn[];
  /** Label of the button that runs the report. */
  submitLabel?: 'Submit' | 'Search' | 'Apply' | 'Generate Report';
  /** The report shows without filters being submitted. */
  autoRun?: boolean;
  /** Heading above the result table; the reports say "Search Results :". */
  resultHeading?: string | null;
  /** Text of an empty result. */
  emptyText?: string;
  /** A report added after a deployment: listed and run only while this release feature is on. */
  feature?: string;
  /** A button beside the submit one that empties the filters and runs again ("Reset Search"). */
  reset?: { label: string; feature?: string };
}

/** A value of a report cell: dates as ISO strings, money as integer cents. */
export type ReportValue = string | number | boolean | null;
export type ReportRow = Record<string, ReportValue>;

export const reportColumn = (
  key: string,
  header: string,
  kind?: ReportColumnKind,
  feature?: string,
): ReportColumn => ({
  key,
  header,
  ...(kind ? { kind } : {}),
  ...(feature ? { feature } : {}),
});

/** The columns of a definition that are live: those without a switch or whose switch is on. */
export const liveColumns = (
  columns: readonly ReportColumn[],
  isOn: (feature: string) => boolean,
): ReportColumn[] => columns.filter((c) => !c.feature || isOn(c.feature));
const c = reportColumn;

const AGENT_COLUMNS = [
  c('masterAgent', 'Master Agent'),
  c('distributor', 'Distributor Agent'),
  c('retailer', 'Retailer Agent'),
  c('employee', 'Agent Name'),
];

/** Login IDs of the sales chain, as the Prepaid Renewed Report ends. */
const LOGIN_COLUMNS = [
  c('masterLogin', 'Master Login Id'),
  c('distributorLogin', 'Distributor Login Id'),
  c('retailerLogin', 'Retailer Login Id'),
  c('employeeLogin', 'Employee Login Id'),
];

/** The invoice types this application makes, by the reference's names. */
export const INVOICE_CATEGORIES = [
  'New Signup Plan Purchase',
  'CSR Plan Purchase',
  'Add-Minutes',
  'Topup Data',
  'Free Topup',
  'Free Topup Data',
  'Equipment Purchase',
  'CSR Bill Pay',
  'ADD CASH WALLET',
  'ADD WALLET SKIP PAYMENT',
  'Ad hoc - Invoice (Other)',
  'Credit Adj',
  'Feature Purchase',
] as const;

/** "Please select a specific period ..." of the reports limited to 90 days. */
export const NINETY_DAYS_MESSAGE =
  'Please select a specific period, ensuring it falls within a consecutive three-month window and does not exceed a maximum duration of 90 days, in order to proceed.';

export const REPORT_DEFS: readonly ReportDef[] = [
  /* ---------------------------- Management reports --------------------------- */
  {
    key: 'activation',
    group: 'management',
    name: 'Activation Report',
    heading: 'Activation Report',
    code: 'PC61',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'dateType',
        label: 'Date',
        options: ['Order Date', 'Activation Date'],
      },
      {
        type: 'select',
        key: 'accountStatus',
        label: 'Account Status',
        options: ['Active', 'Disconnect', 'Dispatched', 'Pending', 'Suspend', 'Cancelled'],
        required: true,
      },
    ],
    columns: [
      c('createdDate', 'Created Date', 'datetime'),
      c('orderDate', 'Order Date', 'datetime'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'ZIP'),
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('email', 'Email'),
      c('alternatePhone', 'Alternate Phone Number'),
      c('deviceId', 'Device ID'),
      c('makeModel', 'Make Model'),
      c('orderStatus', 'Order status'),
      c('planName', 'Plan Name'),
      c('productType', 'Product Type'),
      c('carrier', 'Carrier'),
      c('source', 'Source'),
      c('masterAgent', 'Master Agent'),
      c('distributor', 'Distributor Agent'),
      c('retailer', 'Retailer Agent'),
      c('employee', 'Agent'),
      c('accountStatus', 'Account Status'),
      c('agentLoginId', 'Agent Login ID'),
      c('disconnectReason', 'Disconnect Reason'),
      c('disconnectionDate', 'Deactivation Date', 'datetime'),
      c('activeDays', 'Active Period', 'number'),
    ],
  },
  {
    key: 'customer-line-status',
    group: 'management',
    name: 'Customer Line Status',
    heading: 'Customer Line Status',
    code: 'PC125',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'Select State', allLabel: 'ALL' },
    ],
    columns: [
      c('customerId', 'Cust ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('mdn', 'MDN'),
      c('email', 'Email'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('zip', 'ZIP'),
      c('city', 'City'),
      c('state', 'State'),
      c('planName', 'Plan'),
      c('carrier', 'Carrier'),
      ...AGENT_COLUMNS,
      c('createdDate', 'Approve Date', 'datetime'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('disconnectionDate', 'Disconnection Date', 'datetime'),
      c('disconnectReason', 'Reason'),
      c('accountStatus', 'Account Status'),
      c('isEsim', 'Is Esim(Y/N)', 'yesno'),
      c('productType', 'Enrollment Type'),
    ],
  },
  {
    key: 'customer-list',
    group: 'management',
    name: 'Customer List',
    heading: 'Customer Report',
    code: 'PC126',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'agentChain' },
      {
        type: 'select',
        key: 'dateType',
        label: 'Date Type',
        options: ['Activated Date', 'Disconnect Date'],
      },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      {
        type: 'select',
        key: 'accountStatus',
        label: 'Account Status',
        options: [
          'Active',
          'Inactive',
          'Disconnected',
          'Locked',
          'Suspend',
          'Invalid',
          'Not eligible',
          'Undeliverable',
          'Pending',
          'Dispatched',
        ],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('accountNo', 'ACCOUNT NUMBER'),
      c('sim', 'SIM'),
      c('firstName', 'FIRST NAME'),
      c('lastName', 'LAST NAME'),
      c('address1', 'ADDRESS 1'),
      c('address2', 'ADDRESS 2'),
      c('city', 'CITY'),
      c('state', 'STATE'),
      c('zip', 'ZIP'),
      c('activationDate', 'ACTIVATION DATE', 'datetime'),
      c('disconnectionDate', 'DISCONNECT DATE', 'datetime'),
      c('accountStatus', 'ACCOUNT STATUS'),
      c('carrier', 'CARRIER'),
      c('masterAgent', 'MASTER AGENT'),
      c('mdn', 'MDN'),
      c('primaryPhone', 'PRIMARY PHONE'),
      c('email', 'EMAIL'),
      c('walletBalance', 'Wallet Balance', 'money'),
      c('lastPaymentDate', 'Last Payment Date', 'datetime'),
      c('planName', 'Current Plan'),
    ],
  },
  {
    key: 'enhance-payment',
    group: 'management',
    name: 'Enhance Payment Report',
    heading: 'Enhance Payment Report',
    code: 'PC695',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
      // Walk of 2026-10-27; "Refund Payments" is left out: no payment is refunded here.
      {
        type: 'checkbox',
        key: 'excludeWallet',
        label: 'Exclude Wallet',
        feature: 'reports-extras-2',
      },
      {
        type: 'checkbox',
        key: 'voidPayments',
        label: 'Void Payments',
        feature: 'reports-extras-2',
      },
    ],
    columns: [
      c('amount', 'Amount', 'money'),
      c('tax', 'Tax', 'money'),
      c('totalPaid', 'Total Paid', 'money'),
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('mdn', 'MDN'),
      c('planName', 'Plan Name'),
      c('planPrice', 'Plan Price', 'money'),
      c('receiptNumber', 'Receipt Number'),
      c('paymentMethod', 'Payment Category'),
      c('postedBy', 'Posted By'),
      c('state', 'State'),
      c('paidAt', 'Transaction Date', 'datetime'),
      c('paymentStatus', 'Current Status'),
      c('voidStatus', 'Void Status', undefined, 'reports-extras-2'),
      c('walletApplied', 'Wallet Applied', 'money', 'reports-extras-2'),
      c('discountApplied', 'Discount Applied', 'money', 'reports-extras-2'),
      c('enrollmentMethod', 'Enrollment Method', undefined, 'reports-extras-2'),
      // Walk of 2026-10-27 (third round); BYOD Charge and Partial Payment Details are left out:
      // 212Ez sells no BYOD and takes no partial payment on a sale.
      c('equipmentDiscount', 'Equipment Discount', 'money', 'reports-extras-3'),
      c('taxBreakup', 'Tax Breakup', undefined, 'reports-extras-3'),
      c('retailerId', 'Retailer ID', undefined, 'reports-extras-3'),
      c('lastPurchaseDate', 'Last Purchase Date', 'datetime', 'reports-extras-3'),
    ],
  },
  {
    key: 'oss-api-log',
    group: 'management',
    name: 'OSS API Log',
    heading: 'OSS API Log',
    code: 'PC742',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'status',
        label: 'API Status',
        options: ['Success', 'Fail'],
        emptyLabel: 'All',
      },
      { type: 'text', key: 'api', label: 'API Name', maxLength: 60 },
    ],
    columns: [
      c('api', 'API Name'),
      c('total', 'Total Requests', 'number'),
      c('success', 'Success', 'number'),
      c('fail', 'Fail', 'number'),
    ],
  },
  {
    key: 'payment-by-store',
    group: 'management',
    name: 'Payment Report By Store',
    heading: 'Payment Report By Store',
    code: 'PC553',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('mdn', 'MDN'),
      c('receiptNumber', 'Invoice Number'),
      c('paidAt', 'Transaction Date', 'datetime'),
      c('totalPaid', 'Total', 'money'),
      c('tax', 'Tax', 'money'),
      c('source', 'Source'),
      c('paymentMethod', 'Payment Category'),
      c('store', 'Store Location'),
      c('postedBy', 'Created By'),
    ],
  },
  {
    key: 'revenue',
    group: 'management',
    name: 'Revenue Report',
    heading: 'Revenue Report',
    code: 'PC1186',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    columns: [
      c('accountNo', 'Account Number'),
      c('customerId', 'Customer ID'),
      c('planPrice', 'Plan Price', 'money'),
      c('discount', 'Discount', 'money'),
      c('additional', 'Additional Charges', 'money'),
      c('amount', 'Gross Amount', 'money'),
      c('receiptNumber', 'Invoice No'),
      c('beforeTax', 'Amount due before tax', 'money'),
      c('tax', 'Tax', 'money'),
      c('paymentMethod', 'Payment Type'),
      c('paidAt', 'Billing Date', 'datetime'),
      c('customerName', 'Account Name'),
      c('planName', 'Plan Name'),
      c('paymentStatus', 'Status'),
      c('masterAgent', 'Master Agent'),
      c('distributor', 'Distributor'),
      c('retailer', 'Retailer'),
      c('employee', 'Employee'),
    ],
  },
  {
    key: 'user-login-log',
    group: 'management',
    name: 'User Login Log Report',
    heading: 'User Login Log Report',
    code: 'PC159',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'text', key: 'loginId', label: 'User Login', maxLength: 40 },
      { type: 'userGroup', key: 'groupType', label: 'Group Type', feature: 'report-filters-3' },
    ],
    columns: [
      c('agentName', 'Agent Name'),
      c('loginTime', 'Login Time', 'datetime'),
      c('logoutTime', 'Logout Time', 'datetime'),
      c('activeMinutes', 'Active Time', 'number'),
      c('source', 'Source'),
      c('groupType', 'Group Type'),
      c('ip', 'IP Address'),
    ],
  },

  /*
   * Reports added once top-ups, renewals, wallet payments and the corrected shipping flow
   * existed. Where a definition says `columns are ours`, the reference showed the report's
   * name and filters but its result columns load only after a search on live data, which was
   * never run; those columns are this application's choice.
   */
  {
    key: 'agent-topup',
    group: 'management',
    name: 'Agent-wise Topup Report',
    heading: 'Agent-wise Topup Report',
    code: 'PC197',
    filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }, { type: 'agentChain' }],
    columns: [
      c('amount', 'Amount', 'money'),
      c('mdn', 'MDN'),
      c('customerId', 'Customer ID'),
      c('customerName', 'Customer Name'),
      c('invoiceNo', 'Invoice Number'),
      c('paymentMethod', 'Method'),
      c('purchaseType', 'Purchase Type'),
      c('purchaseName', 'Purchase Name'),
      c('source', 'Source'),
      c('agent', 'Agent'),
      c('state', 'State'),
      c('email', 'Email'),
      c('at', 'Transaction Date', 'datetime'),
    ],
  },
  {
    key: 'notification-report',
    group: 'management',
    name: 'Email and SMS Notification Report',
    heading: 'Email and SMS Notification Report',
    code: 'PC668',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'channel',
        label: 'Notification Type',
        options: ['EMAIL', 'SMS'],
        emptyLabel: 'All',
      },
      // Walk of 2026-10-27; the reference's "TG5 Alert Notification" without the vendor's name.
      {
        type: 'select',
        key: 'category',
        label: 'Notification Category',
        options: ['Customer Notification', 'Alert Notification'],
        emptyLabel: 'All',
        feature: 'monitoring-alerts',
      },
      { type: 'text', key: 'event', label: 'Event Type', maxLength: 80 },
      {
        type: 'select',
        key: 'status',
        label: 'Sent Status',
        options: ['Success', 'Process', 'Pending', 'Failed', 'Cancelled'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('channel', 'Notification Type'),
      c('event', 'Event Type'),
      c('subject', 'Subject'),
      c('total', 'Total', 'number'),
    ],
  },
  {
    // Columns of the reference (appendix 13.11). "Invoice Category" lists the invoice types
    // this application makes (the reference has 63); its "Advance Search" (amount criteria)
    // is not built.
    key: 'invoice-report',
    group: 'management',
    name: 'Invoice Report',
    heading: 'Invoice Report',
    code: 'PC693',
    filters: [
      {
        type: 'select',
        key: 'category',
        label: 'Invoice Category',
        options: INVOICE_CATEGORIES,
        emptyLabel: 'All',
      },
      {
        type: 'select',
        key: 'paymentStatus',
        label: 'Payment Status',
        options: ['Paid', 'Unpaid'],
        emptyLabel: 'All',
      },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
      {
        type: 'select',
        key: 'invoiceDays',
        label: 'Select Invoice Days',
        options: ['Last 30 days', 'Last 60 days', 'Last 90 days', 'Custom'],
      },
      { type: 'dateRange', key: 'date', label: 'Date Range Duration (90 Days only)' },
      // "Advance Search" of the walk of 2026-10-27: the invoice amount, in dollars.
      {
        type: 'text',
        key: 'amountFrom',
        label: 'From amount',
        maxLength: 10,
        feature: 'reports-extras-2',
      },
      {
        type: 'text',
        key: 'amountTo',
        label: 'To amount',
        maxLength: 10,
        feature: 'reports-extras-2',
      },
    ],
    columns: [
      c('invoiceNo', 'Invoice No.'),
      c('customerId', 'Customer Id.'),
      c('invoiceType', 'Invoice Type'),
      c('particular', 'Invoice Particular'),
      c('amount', 'Amount', 'money'),
      c('fromDate', 'From Date', 'date'),
      c('dueDate', 'Due Date', 'date'),
      c('createdAt', 'Created Date', 'datetime'),
      c('topupDate', 'Topup Date', 'datetime'),
      c('createdBy', 'Created By'),
      c('source', 'Source'),
      c('paidUsing', 'Paid Using'),
      c('paidStatus', 'Paid/Unpaid'),
      c('productType', 'Product Type'),
      c('planName', 'Plan Name'),
    ],
  },
  {
    // The reference's 48 columns less the ones 212Ez keeps no data for (UCCID, Account
    // Status Pre / Post, Before Renew Date, Advance Month, the grace expiry dates, the
    // "(Config)" columns, the cron, plan-change and external-transaction columns).
    key: 'prepaid-renewed',
    group: 'management',
    name: 'Prepaid Renewed Report',
    heading: 'Prepaid Renewed Report',
    code: 'PC670',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Transaction Date Range' },
      {
        type: 'select',
        key: 'method',
        label: 'Payment Method',
        options: ['WALLET', 'MONEY ORDER', 'MONEY GRAM', 'SKIP'],
        emptyLabel: 'All',
      },
      {
        type: 'select',
        key: 'renewStatus',
        label: 'Plan Renew Status',
        options: ['Plan Purchased from Autopay'],
        emptyLabel: 'Select',
      },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('mdn', 'MDN'),
      c('sim', 'SIM'),
      c('carrier', 'Carrier'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('transactionDate', 'Transaction Date', 'datetime'),
      c('oldPlan', 'Old Plan'),
      c('newPlan', 'New Plan'),
      c('afterRenewDate', 'After Renew Date', 'datetime'),
      c('accountType', 'Account Type'),
      c('renewalType', 'Renewal Type'),
      c('planPrice', 'Plan Price ($)', 'money'),
      c('tax', 'Tax Amount ($)', 'money'),
      c('gross', 'Gross Amount ($)', 'money'),
      c('discounted', 'Discounted Amount ($)', 'money'),
      c('paymentMethod', 'Payment Method'),
      c('preWallet', 'Pre Wallet Balance ($)', 'money'),
      c('postWallet', 'Post Wallet Balance ($)', 'money'),
      c('transactionId', 'Transaction ID'),
      c('invoiceNo', 'Invoice No'),
      c('paymentStatus', 'Payment Status'),
      c('requestedBy', 'Requested By'),
      c('source', 'Source'),
      ...LOGIN_COLUMNS,
    ],
  },
  {
    // The reference's columns less "Credit Card" detail beyond the primary saved card's last
    // four digits (no card is charged here: Auto Pay pays from the wallet).
    key: 'autopay',
    group: 'management',
    name: 'Autopay Report',
    heading: 'AutoPay',
    code: 'PC665',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      {
        type: 'select',
        key: 'autopayType',
        label: 'Auto Pay Type',
        options: ['Active Auto Pay Customer', 'Cancel Auto Pay Customer'],
        emptyLabel: 'Select',
      },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
      { type: 'text', key: 'cardLast4', label: 'Card Last 4 Digits', maxLength: 4 },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('creditCard', 'Credit Card'),
      c('mdn', 'MDN'),
      c('address', 'Address'),
      c('zip', 'Zipcode'),
      c('city', 'City'),
      c('state', 'State'),
      c('autopayStatus', 'Autopay Status'),
      c('purchaseType', 'Purchase Type'),
      c('setupDate', 'Autopay Setup Date', 'datetime'),
      c('planExpiry', 'Plan Expiry Date', 'date'),
      c('planName', 'Plan Name'),
      c('planCode', 'Plan Code'),
      c('createdBy', 'Create By'),
      c('source', 'Source'),
      c('status', 'Status'),
    ],
  },
  {
    // The reference's columns less Last 4 Numbers Of Card, Account Number and Routing Number:
    // Auto Pay here pays from the wallet. A failed attempt is a line with its reason.
    key: 'autopay-transaction',
    group: 'management',
    name: 'Autopay Transaction Report',
    heading: 'AutoPay Transaction',
    code: 'PC666',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('mdn', 'MDN'),
      c('enrollmentId', 'Enrollment ID'),
      c('customerName', 'Customer Name'),
      c('state', 'State'),
      c('carrier', 'Carrier'),
      c('planName', 'Plan Name'),
      c('amountDue', 'Amount Due (Plan Amount)', 'money'),
      c('charged', 'Charged Amount', 'money'),
      c('chargedAt', 'Charged Date', 'datetime'),
      c('agent', 'Agent Name'),
      c('paymentStatus', 'Payment Status'),
      c('reason', 'Reason'),
      c('autopayStatus', 'Autopay Status'),
      c('autopayUpdated', 'Autopay Update Datetime', 'datetime'),
    ],
  },
  {
    // The reference's columns less Old MDN / New MDN (one MDN here) and UCCID.
    key: 'prepaid-next-renew',
    group: 'management',
    name: 'Prepaid Next Renew Report',
    heading: 'Prepaid Next Renew Report',
    code: 'PC671',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      // The reference's spelling. Assumption (its rule was not seen): Exipry, the plan has not
      // run out; Grace, it ran out within the plan's grace period; Cancel, after it.
      {
        type: 'select',
        key: 'renewOn',
        label: 'Prepaid Plan Renew on',
        options: ['Exipry', 'Grace', 'Cancel'],
        multiple: true,
        feature: 'reports-extras-3',
      },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('mdn', 'MDN'),
      c('sim', 'SIM'),
      c('carrier', 'Carrier'),
      c('accountStatus', 'Account Status'),
      c('currentPlan', 'Current Plan'),
      c('requestedPlan', 'Requested Plan'),
      c('requestedPrice', 'Requested Plan Price ($)', 'money'),
      c('statusOnExpiry', 'Plan Status On Expiry (Config)'),
      c('planExpiry', 'Plan Expiry Date', 'date'),
      c('statusOnGrace', 'Plan Status On Grace (Config)'),
      c('graceExpiry', 'Grace Expiry Date', 'date'),
      c('renewOn', 'Prepaid Plan Renew On', undefined, 'reports-extras-3'),
      c('nextRenewDate', 'Next Renew Date', 'date'),
      c('activationDate', 'Activated Date', 'datetime'),
      c('walletBalance', 'Wallet Balance ($)', 'money'),
      c('autopay', 'AutoPay'),
    ],
  },
  {
    // Columns are ours.
    key: 'sales-channel',
    group: 'management',
    name: 'Sales Channel Report',
    heading: 'Sales Channel Report',
    code: 'PC131',
    filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
    columns: [
      c('channel', 'Sales Channel'),
      c('total', 'Total Enrollment', 'number'),
      c('complete', 'Completed', 'number'),
      c('incomplete', 'Incomplete', 'number'),
    ],
  },
  {
    // The reference's columns include the place and device of the user, which are not
    // recorded here. Filters are ours.
    key: 'user-activity',
    group: 'management',
    name: 'User Activity Report',
    heading: 'User Activity Report',
    code: 'PC1200',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'text', key: 'loginId', label: 'User Login ID', maxLength: 40 },
    ],
    columns: [
      c('entity', 'Event Type'),
      c('action', 'Event Name'),
      c('loginId', 'User Login ID'),
      c('ip', 'IP Address'),
      c('at', 'Activity time', 'datetime'),
    ],
  },

  /* ----------------------------- Customer reports ---------------------------- */
  {
    key: 'account-activations',
    group: 'customer',
    name: 'Account Activations Report',
    heading: 'Account Activations Report',
    code: 'PC962',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },
      { type: 'dateRange', key: 'date', label: 'Search By Date' },
    ],
    columns: [
      c('enrollmentId', 'Enrollment Id'),
      c('accountNo', 'Account Number'),
      c('customerName', 'Customer Name'),
      c('state', 'State'),
      c('zip', 'ZIP'),
      c('planName', 'Plan'),
      c('accountStatus', 'Current Status'),
      c('orderDate', 'Enrollment Date', 'datetime'),
      c('activationDate', 'Activation date', 'datetime'),
      c('isEsim', 'eSIM(Y/N)', 'yesno'),
    ],
  },
  {
    key: 'customer-order',
    group: 'customer',
    name: 'Customer Order',
    heading: 'Order Summary Report',
    code: 'PC136',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
    ],
    columns: [
      c('state', 'State'),
      c('new', 'New', 'number'),
      c('activated', 'Activated', 'number'),
      c('pending', 'Pending', 'number'),
    ],
  },
  {
    key: 'customers-reactivation',
    group: 'customer',
    name: 'Customers Reactivation Report',
    heading: 'Reactivation Report (New Activation)',
    code: 'PC139',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'ALL' },
      { type: 'agentChain' },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('mdn', 'MDN'),
      c('planName', 'Plan Name'),
      c('zip', 'ZIP'),
      c('city', 'City'),
      c('state', 'State'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('reconnectionDate', 'Reconnection Date', 'datetime'),
      c('masterAgent', 'Master Agent'),
      c('retailer', 'Retailer'),
      c('distributor', 'Distributor'),
      c('employee', 'Employee'),
    ],
  },
  {
    key: 'disconnection-by-store',
    group: 'customer',
    name: 'Disconnection Report By Store',
    heading: 'Disconnection Report By Store',
    code: 'PC563',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    columns: [
      // The reference spells this header "Disconection Date".
      c('disconnectionDate', 'Disconection Date', 'datetime'),
      c('disconnectBy', 'Disconnect By'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'ZIP'),
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('deviceId', 'Device ID'),
      c('planName', 'Plan'),
      c('carrier', 'Carrier'),
      c('source', 'Source'),
      ...AGENT_COLUMNS,
    ],
  },
  {
    key: 'non-lifeline-new-activation',
    group: 'customer',
    name: 'Non Life-line Report (New Activation)',
    heading: 'Non Life-line Report (New Activation)',
    code: 'PC144',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'ALL' },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('zip', 'Zipcode'),
      c('city', 'City'),
      c('state', 'State'),
      c('planName', 'Plan'),
      c('activationDate', 'Activation Date', 'datetime'),
    ],
  },
  {
    key: 'order-status-summary',
    group: 'customer',
    name: 'Order Status Summary',
    heading: 'Order Status Summary',
    code: 'PC1185',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Search By Date' },
      {
        type: 'select',
        key: 'source',
        label: 'Enrollment Source',
        options: ['Field', 'Web', 'API'],
        required: true,
      },
    ],
    columns: [
      c('total', 'Total Enrollment', 'number'),
      c('approved', 'Approved', 'number'),
      c('approvedPct', 'Approved %'),
      c('rejected', 'Rejected', 'number'),
      c('rejectedPct', 'Rejected %'),
      c('incomplete', 'Incomplete', 'number'),
      c('incompletePct', 'Incomplete %'),
    ],
  },
  {
    key: 'payment-detail',
    group: 'customer',
    name: 'Payment Detail Report',
    heading: 'Payment Detail Report',
    code: 'PC951',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('paymentMethod', 'Transaction Type'),
      c('paidAt', 'Transaction Date', 'datetime'),
      c('receiptNumber', 'Transaction ID'),
      c('paymentStatus', 'Transaction Status'),
      c('amount', 'Amount', 'money'),
      c('address1', 'Address'),
      c('source', 'Source'),
    ],
  },
  {
    // The reference's 21 columns less those 212Ez keeps no data for. No card is charged here (a
    // declined payment is a renewal Auto Pay or the wallet could not pay), so Name On Card, Card
    // Type, Declined Card Last 4 Digits, Transaction ID and Code are left out; so is Amount (the
    // refused charge is not kept). "Enrollment Type" {All, Failed, Completed} is left out: no
    // payment is declined at enrollment here.
    key: 'payment-declined',
    group: 'customer',
    name: 'Payment Declined Report',
    heading: 'Payment Declined Report',
    code: 'PC932',
    feature: 'report-payment-declined',
    filters: [{ type: 'dateRange', key: 'date', label: 'Search By Date' }],
    columns: [
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enroll Id'),
      c('mdn', 'MDN'),
      c('email', 'Email'),
      c('transactionType', 'Transaction Type'),
      c('transactionDate', 'Transaction Date', 'datetime'),
      c('address1', 'Address 1'),
      c('address2', 'Address 2'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'ZIP'),
      c('source', 'Source'),
      c('reason', 'Reason'),
      c('primaryCard', 'Primary Card Last 4 Digits'),
    ],
  },
  {
    key: 'plan-list',
    group: 'customer',
    name: 'Plan List Report',
    heading: 'Plan List Report',
    code: 'PC957',
    filters: [],
    autoRun: true,
    columns: [
      c('planId', 'Plan Id'),
      c('planName', 'Plan Name'),
      c('planState', 'Plan State'),
      c('planPrice', 'Plan Price', 'money'),
      c('planCode', 'Plan Code'),
      c('planStatus', 'Plan Status'),
      c('displayStatus', 'Display Status', 'yesno'),
      c('agent', 'Agent', 'yesno'),
      c('app', 'App', 'yesno'),
      c('csr', 'CSR', 'yesno'),
      c('api', 'API', 'yesno'),
      c('website', 'Website', 'yesno'),
      c('enrollmentPortal', 'Enrollment Portal', 'yesno'),
      c('ivr', 'IVR', 'yesno'),
      c('active', 'Active Customer', 'number'),
      c('dispatched', 'Dispatched Customer', 'number'),
      c('suspended', 'Suspend Customer', 'number'),
      c('inactive', 'Inactive Customer', 'number'),
      c('lastEnrollment', 'Last Enrollment Date on the Plan', 'datetime'),
    ],
  },
  {
    key: 'prepaid-plan-payment-status',
    group: 'customer',
    name: 'Prepaid plan payment status',
    heading: 'Prepaid plan payment status',
    code: 'PC793',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      {
        type: 'select',
        key: 'paymentStatus',
        label: 'Payment Status',
        options: ['Paid', 'Skipped'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('customerId', 'Customer'),
      c('accountStatus', 'Account Status'),
      c('paymentMethod', 'Payment Method'),
      c('paymentStatus', 'Payment Status'),
      c('carrier', 'Carrier'),
      c('totalPaid', 'Total Pay Amount', 'money'),
      c('receiptNumber', 'Invoice No'),
      c('paidAt', 'Update Date', 'datetime'),
      c('source', 'Source'),
    ],
  },
  {
    key: 'wallet-balance',
    group: 'customer',
    name: 'Wallet Balance Report',
    heading: 'Wallet Balance Report',
    code: 'PC935',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters',
      },

      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'accountStatus',
        label: 'Account Status',
        options: ['Active', 'Inactive', 'Suspend', 'Dispatched'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('enrollmentId', 'Enroll Id'),
      c('mdn', 'MDN'),
      c('city', 'City'),
      c('state', 'State'),
      c('accountStatus', 'Account Status'),
      c('walletBalance', 'Wallet Balance', 'money'),
      c('discountBalance', 'Discount Balance', 'money', 'reports-extras-2'),
      c('accountNo', 'Account Number'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address'),
      c('email', 'Email'),
      c('lastPaymentDate', 'Last Payment Date', 'datetime'),
      c('createdDate', 'Approve Date', 'datetime'),
      c('disconnectionDate', 'Disconnection Date', 'datetime'),
    ],
  },

  {
    key: 'paid-equipment',
    group: 'customer',
    name: 'Paid - Equipment',
    heading: 'Paid - Equipment',
    code: 'PC317',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'method',
        label: 'Payment Method',
        options: ['Cash', 'Money Order', 'Money Gram', 'Wallet'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('amount', 'Amount (incl. tax)', 'money'),
      c('tax', 'Tax', 'money'),
      c('equipment', 'Phone Model'),
      c('paymentMethod', 'Payment Method'),
      c('at', 'Date', 'datetime'),
      c('address1', 'Address'),
      c('source', 'Source'),
      c('status', 'Status'),
    ],
  },
  {
    // The filter is ours; the columns are the reference's that have data here.
    key: 'wallet-details',
    group: 'customer',
    name: 'Wallet Details Report',
    heading: 'Wallet Details Report',
    code: 'PC619',
    filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
    columns: [
      c('customerId', 'Customer ID'),
      c('mdn', 'MDN'),
      c('enrollmentId', 'Enroll ID'),
      c('at', 'Payment Date', 'datetime'),
      c('amount', 'Amount', 'money'),
      c('type', 'Transaction Type'),
      c('description', 'Description'),
      c('by', 'Purchase By'),
    ],
  },
  {
    key: 'order-shipment',
    group: 'customer',
    name: 'Order Shipment Report',
    heading: 'Order Shipment Report',
    code: 'PC929',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Request Date' },
      { type: 'text', key: 'enrollmentId', label: 'Enrollment ID', maxLength: 20 },
    ],
    columns: [
      c('customerId', 'Cust ID'),
      c('accountNo', 'Account #'),
      c('enrollmentId', 'Enrollment ID'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'ZIP'),
      c('orderedAt', 'Date/Time Ordered', 'datetime'),
      c('shippedAt', 'Date/Time Shipped', 'datetime'),
      c('trackingNumber', 'Track ID'),
      c('sim', 'SIM'),
    ],
  },
  {
    // Note Status: Active is a note in force, Inactive one archived with Mark Void.
    key: 'customer-note',
    group: 'customer',
    name: 'Customer Note',
    heading: 'Customers Note',
    code: 'PC145',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'text', key: 'noteType', label: 'Note Type', maxLength: 100 },
      {
        type: 'select',
        key: 'noteStatus',
        label: 'Note Status',
        options: ['Active', 'Inactive'],
        emptyLabel: 'Both',
        feature: 'report-filters-3',
      },
    ],
    resultHeading: 'Total Count :',
    columns: [c('noteType', 'Note Type'), c('count', 'Count', 'number')],
  },
  {
    key: 'customer-disconnections',
    group: 'customer',
    name: 'Customer Disconnections with Reason',
    heading: 'Customer Disconnections with Reason',
    code: 'PC157',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
    ],
    submitLabel: 'Search',
    resultHeading: 'Total Count:',
    columns: [
      c('customerId', 'Cust ID'),
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('firstName', 'First Name'),
      c('lastName', 'Last Name'),
      c('address1', 'Address1'),
      c('address2', 'Address2'),
      c('zip', 'Zip Code'),
      c('city', 'City'),
      c('state', 'State'),
      c('planName', 'Plan'),
      c('carrier', 'Carrier'),
      c('masterAgent', 'Master Agent Name'),
      c('distributor', 'Distributor Agent Name'),
      c('retailer', 'Retailer Agent Name'),
      c('employee', 'Agent Name'),
      c('approveDate', 'Approve Date', 'datetime'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('disconnectionDate', 'Disconnection Date', 'datetime'),
      c('disconnectReason', 'Reason'),
      c('enrollmentType', 'Enrollment Type'),
    ],
  },
  {
    // "Current Billing Code": the carrier code the customer's plan is sold under.
    key: 'billing-code',
    group: 'customer',
    name: 'Customer Billing Code/PWG Cost Plan Report',
    heading: 'Customer Billing Code/PWG Cost Plan Report',
    code: 'PC1140',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'text', key: 'customerId', label: 'Customer Id', maxLength: 11 },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'accountStatus',
        label: 'Account Status',
        options: ['Active', 'Suspend'],
        emptyLabel: 'All',
      },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('customerName', 'Customer Name'),
      c('accountStatus', 'Account status'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('mdn', 'MDN'),
      c('sim', 'SIM'),
      c('planName', 'Plan'),
      c('billingCode', 'Current Billing Code'),
    ],
  },
  {
    // A summary, as the reference: Active customers per state and plan.
    key: 'active-customer-with-plan',
    group: 'customer',
    name: 'Active Customer With Plan',
    heading: 'Active Customer With Plan',
    code: 'PC40',
    filters: [
      {
        type: 'select',
        key: 'accountType',
        label: 'Account Type',
        options: ['Prepaid', 'Postpaid', 'Lifeline', 'Charity'],
        emptyLabel: 'All',
        feature: 'account-type-filters-2',
      },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'state', key: 'state', label: 'Select State', allLabel: 'All' },
    ],
    columns: [c('state', 'State'), c('planName', 'Plan'), c('total', 'Total Customer', 'number')],
  },
  {
    // Columns are ours.
    key: 'swap-device',
    group: 'customer',
    name: 'Swap Device Report',
    heading: 'Swap Device Report',
    code: 'PC503',
    filters: [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'text', key: 'customerId', label: 'Customer ID', maxLength: 11 },
    ],
    columns: [
      c('customerId', 'Customer ID'),
      c('customerName', 'Customer Name'),
      c('mdn', 'MDN'),
      c('change', 'Change'),
      c('details', 'Details'),
      c('by', 'Changed By'),
      c('at', 'Date', 'datetime'),
    ],
  },

  /* ------------------------------- Agent reports ----------------------------- */
  {
    key: 'agent-sales',
    group: 'agent',
    name: 'Agent Sales Report',
    heading: 'Agent Sales Report',
    code: 'PC1036',
    filters: [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      {
        type: 'select',
        key: 'status',
        label: 'Enrollment Status',
        options: ['Handover', 'Approved', 'Pending Review', 'Rejected'],
        emptyLabel: 'Select',
      },
    ],
    columns: [
      c('enrollmentId', 'Enroll ID'),
      c('customerId', 'Cust ID'),
      c('status', 'Status'),
      c('orderDate', 'Order Date', 'datetime'),
      c('activationDate', 'Activation Date', 'datetime'),
      c('lastActionDate', 'Last Action Date', 'datetime'),
      c('amount', 'Amount', 'money'),
      c('paymentMethod', 'Type'),
      c('advanceMonths', 'Advance Month', 'number'),
      c('sim', 'SIM'),
      c('planName', 'Plan Name'),
      c('productType', 'Product Type'),
      c('source', 'Source'),
      c('age', 'Age', 'number'),
      c('masterAgent', 'Master'),
      c('distributor', 'Distributor'),
      c('retailer', 'Retailer'),
      c('employee', 'Employee'),
    ],
  },

  {
    // Heading and grouping of the reference: per state and master agent, a "Sub Total" line
    // after each state and a "Total" line. The reference puts each carrier in a column of its
    // own; here each carrier is a line.
    key: 'topups-by-state',
    group: 'agent',
    name: 'Top Ups by State',
    heading: 'Total Amount By State',
    code: 'PC156',
    filters: [
      { type: 'dateRange', key: 'date', label: 'Date Range' },
      { type: 'agentChain' },
      { type: 'state', key: 'state', label: 'State', allLabel: 'All' },
    ],
    columns: [
      c('state', 'State'),
      c('masterAgent', 'Master Agent'),
      c('carrier', 'Carrier'),
      c('amount', 'Total Amount', 'money'),
    ],
  },
  {
    // A summary per sales chain, as the reference ("Sales Chain", "Enrollment Activity").
    // Activated: completed; Rejected: refused in the review queue; Pending: the rest.
    key: 'enrollment-activity',
    group: 'agent',
    name: 'Enrollment Activity Report',
    heading: 'Enrollment Activity Report',
    code: 'PC151',
    filters: [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Search By Enrollment Date' },
      { type: 'state', key: 'state', label: 'Select State', allLabel: 'ALL' },
    ],
    submitLabel: 'Search',
    resultHeading: 'Total Count:',
    columns: [
      c('masterAgent', 'Master Agent Name'),
      c('distributor', 'Distributor Name'),
      c('retailer', 'Retailer Name'),
      c('employee', 'Employee Name'),
      c('total', 'Total Enrollment', 'number'),
      c('activated', 'Activated Enrollment', 'number'),
      c('rejected', 'Rejected Enrollment', 'number'),
      c('pending', 'Pending Enrollment', 'number'),
    ],
  },
  {
    // Per user who took the orders (Activated: completed; Rejected: refused in review).
    key: 'activity-report',
    group: 'agent',
    name: 'Activity Report',
    heading: 'Activity Report',
    code: 'PC191',
    filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
    resultHeading: 'Total Count :',
    columns: [
      c('employee', 'Employee Name'),
      c('total', 'Total Enrollment', 'number'),
      c('activated', 'Activated Enrollment', 'number'),
      c('rejected', 'Rejected Enrollment', 'number'),
      c('pending', 'Pending Enrollment', 'number'),
    ],
  },
  {
    // The reference's columns less "Emp ID": users here have no employee number.
    key: 'agent-order',
    group: 'agent',
    name: 'Agent Order Report',
    heading: 'Agent Order Report',
    code: 'PC789',
    filters: [
      { type: 'text', key: 'loginId', label: 'Search By Employee Login ID :', maxLength: 50 },
      {
        type: 'select',
        key: 'days',
        label: 'Search By Days :',
        options: ['All', 'Last 10 Days', 'Last 15 Days', 'Last 30 Days', 'Last 45 Days'],
      },
    ],
    submitLabel: 'Search',
    columns: [
      c('fullName', 'Full Name (Login ID)'),
      c('registeredAt', 'Employee Registration Date', 'datetime'),
      c('lastCustomerAt', 'Last Customer Created Date', 'datetime'),
    ],
  },
  {
    // Columns are ours.
    key: 'handover-activity',
    group: 'agent',
    name: 'Handover Activity Report',
    heading: 'Handover Activity Report',
    code: 'PC152',
    filters: [
      { type: 'agentChain' },
      { type: 'dateRange', key: 'date', label: 'Search By Date Range' },
      { type: 'state', key: 'state', label: 'Select State', allLabel: 'ALL' },
      { type: 'carrier', key: 'carrier', label: 'Select Carrier' },
    ],
    submitLabel: 'Search',
    columns: [
      c('enrollmentId', 'Enrollment ID'),
      c('customerName', 'Customer Name'),
      c('state', 'State'),
      c('handover', 'Handover'),
      c('sim', 'SIM'),
      c('mdn', 'MDN'),
      c('carrier', 'Carrier'),
      c('planName', 'Plan'),
      c('status', 'Status'),
      c('orderDate', 'Date', 'datetime'),
      ...AGENT_COLUMNS,
    ],
  },

  {
    // Columns are ours, and so is how commission is earned (see users/commission.ts). The
    // Account Type filter of the reference is left out.
    key: 'all-commission-matrix',
    group: 'agent',
    name: 'All Commission Matrix',
    heading: 'All Commission Matrix',
    code: 'PC798',
    filters: [{ type: 'agentChain' }, { type: 'dateRange', key: 'date', label: 'Date Range' }],
    columns: [
      c('agent', 'Agent'),
      c('loginId', 'Login ID'),
      c('level', 'Level'),
      c('matrixName', 'Matrix Name'),
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enrollment ID'),
      c('kind', 'Commission For'),
      c('category', 'Category'),
      c('month', 'Month'),
      c('amount', 'Amount', 'money'),
      c('at', 'Date', 'datetime'),
    ],
  },

  /* ------------------------------------ Logs --------------------------------- */
  {
    key: 'rest-api-log',
    group: 'log',
    name: 'REST API Log',
    heading: 'REST API Log',
    code: 'PC808',
    reset: { label: 'Reset Search', feature: 'api-log-reset' },
    filters: [
      { type: 'text', key: 'api', label: 'API', maxLength: 60 },
      { type: 'text', key: 'enrollmentId', label: 'Enrollment ID', maxLength: 20 },
      { type: 'dateRange', key: 'date', label: 'Date' },
    ],
    submitLabel: 'Search',
    autoRun: true,
    columns: [
      c('api', 'API'),
      c('action', 'Action'),
      c('request', 'Request'),
      c('response', 'Response'),
      c('enrollmentId', 'Enrollment ID'),
      c('requestTime', 'Request Time', 'datetime'),
      c('durationMs', 'Response Time', 'number'),
      c('status', 'Response Status'),
    ],
  },
  {
    key: 'usps-api-log',
    group: 'log',
    name: 'USPS API Log',
    heading: 'USPS API Log',
    code: 'PC1101',
    filters: [
      { type: 'text', key: 'enrollmentId', label: 'Enrollment ID', maxLength: 20 },
      {
        type: 'select',
        key: 'status',
        label: 'Status',
        options: ['Success', 'Fail'],
        emptyLabel: 'All',
      },
      { type: 'dateRange', key: 'date', label: 'Select Date' },
    ],
    autoRun: true,
    columns: [
      c('enrollmentId', 'Enroll Id'),
      c('request', 'Request'),
      c('response', 'Response'),
      c('status', 'Status'),
      c('requestTime', 'Request Datetime', 'datetime'),
    ],
  },
  {
    // The reference's Management report (walk of 2026-10-23), with its columns; "Refunded Type"
    // is Full or Partial (switch payment-refunds).
    key: 'payment-refunds',
    group: 'management',
    name: 'Payment Refunds Report',
    heading: 'Payment Refunds Report',
    code: 'PC655',
    feature: 'payment-refunds',
    filters: [{ type: 'dateRange', key: 'date', label: 'Date Range' }],
    columns: [
      c('invoiceNo', 'Invoice No'),
      c('paymentDate', 'Payment Date Time', 'datetime'),
      c('paymentMethod', 'Payment Method'),
      c('purchaseType', 'Purchase Type'),
      c('customerId', 'Customer ID'),
      c('enrollmentId', 'Enroll ID'),
      c('mdn', 'MDN'),
      c('city', 'City'),
      c('state', 'State'),
      c('zip', 'Zip Code'),
      c('transactionId', 'Refunded Transaction ID'),
      c('amount', 'Refunded Amount', 'money'),
      c('refundedBy', 'Refunded By'),
      c('refundType', 'Refunded Type'),
      c('reason', 'Refund Reason'),
      c('refundedAt', 'Refunded Date Time', 'datetime'),
      c('refundInvoiceNo', 'Refund Invoice No'),
    ],
  },
];

const BY_KEY = new Map(REPORT_DEFS.map((d) => [d.key, d]));
export const getReportDef = (key: string): ReportDef | undefined => BY_KEY.get(key);

/** Adds list screens defined elsewhere (`screens.ts`) to the same registry. */
export function registerReportDefs(defs: readonly ReportDef[]): void {
  for (const d of defs) BY_KEY.set(d.key, d);
}

/**
 * Reports whose page lists the files generated from it, "Date Period | Generated Date | Download
 * Link" (walk of 2026-10-27; switch `report-generated-files`). Assumption: a file is generated
 * when a user downloads the report's CSV, and each user sees their own files.
 */
export const GENERATED_FILE_REPORTS: readonly string[] = [
  'agent-sales',
  'agent-topup',
  'autopay',
  'autopay-transaction',
  'customer-line-status',
  'customer-list',
  'customers-reactivation',
  'enhance-payment',
  'paid-equipment',
  'payment-declined',
  'payment-detail',
  'payment-by-store',
  'plan-list',
  'prepaid-renewed',
  'prepaid-plan-payment-status',
  'revenue',
  'user-activity',
  'user-login-log',
  'wallet-balance',
  'wallet-details',
];

export interface GeneratedReportFileDto {
  id: string;
  /** The report's date range when the file was made; null for an open end. */
  dateFrom: string | null;
  dateTo: string | null;
  generatedAt: string;
  rows: number;
}

/** Reports of the All Reports catalogue (the logs have their own menu entries). */
export const CATALOGUE_REPORTS = REPORT_DEFS.filter((d) => d.group !== 'log');

export const isStateCode = (value: string): boolean => US_STATE_CODES.includes(value);

export interface ReportResult {
  data: ReportRow[];
  page: number;
  pageSize: number;
  total: number;
}
